/** @module store/communities */
import * as apiCommunities from '@/api/communities'

const state = () => ({})

/**
 * A Community data-struct.
 *
 * @constructor
 *
 * @param {object} params
 * @param {integer} params.id is the communityId
 * @param {string} params.name the given CommunityName
 * @param {integer} params.rootGroupId is the Id of the community's root group
 */
export function Community({ id, name, rootGroupId }) {
  this.id = id
  this.name = name
  this.rootGroupId = rootGroupId
}

const mutations = {
  /**
   * Mutation to reset the communities store
   *
   * @function
   *
   */
  RESET_COMMUNITIES: (state) => {
    for (const cId in state) {
      delete state[cId]
    }
  },
  /**
   * Mutation to set a community into the store
   *
   * @function
   *
   * @param {Community} c
   *
   */
  SET_COMMUNITY: (state, c) => {
    state[c.id] = c
  },
  /**
   * Mutation to set a number of communities into the store
   *
   * @function
   *
   * @param {Community[]} cs
   *
   */
  SET_COMMUNITIES: (state, cs) => {
    for (const c of cs) {
      state[c.id] = c
    }
  },
  /**
   * Mutation to change the name of a community in the store
   *
   * @function
   *
   * @param {integer} communityId
   * @param {String} name
   *
   */
  SET_COMMUNITY_NAME: (state, { communityId, name }) => {
    state[communityId].name = name
  }
}

const getters = {
  /**
   * Getter to return a community
   *
   * @function
   *
   * @param {integer} id
   *
   * @return {Community}
   */
  getCommunity: (state) => {
    return (id) => {
      return state[id]
    }
  },
  /**
   * Getter to return all communities as a list
   *
   * @function
   *
   * @return {Object[]} [{ id, name }]
   */
  getCommunityList: (state) => {
    const list = []
    for (const id in state) {
      list.push({ id, name: state[id].name })
    }
    return list
  },
  /**
   * Getter to return all communities as Options with rootGroupId as value
   *
   * @function
   *
   * @return {Object[]} [{ id, name }]
   */
  communityRootGroupSelect: (state) => {
    const options = []
    for (const id in state) {
      options.push({ value: state[id].rootGroupId, label: state[id].name })
    }
    return options
  },
  getCommunityEmssByGroups: (state, getters, rootState, rootGetters) => {
    return (id) => {
      const emssByGroups = {}
      const rootGroupId = parseInt(state?.[id]?.rootGroupId)

      if (!rootGroupId) {
        return emssByGroups
      }

      const gIds = rootGetters['energyManagementSystemGroups/getGroupIdsFromRoot'](rootGroupId)

      for (const gId of gIds) {
        emssByGroups[gId] = {}
        emssByGroups[gId].id = gId
        emssByGroups[gId].name = rootGetters['energyManagementSystemGroups/getEmsGroup'](gId)?.name

        emssByGroups[gId].emss = {}

        const emss = rootGetters['energyManagementSystems/getEmssForGroup'](gId)
        for (const emsId in emss) {
          emssByGroups[gId].emss[emsId] = emss[emsId]
        }
      }

      return emssByGroups
    }
  }
}

const actions = {
  /**
   * Action to request a community and store it
   *
   * @function
   *
   * @params {String} ctxt
   * @params {Integer} communityId
   *
   * @return {Community}
   */
  async getCommunity({ commit }, { ctxt, communityId }) {
    const rsp = await apiCommunities.getCommunity({
      communityId,
      ctxt
    })
    const data = rsp.payload.data

    const c = new Community({
      id: data.id,
      name: data.attributes.name,
      rootGroupId: data.relationships.group.data.id
    })

    commit('SET_COMMUNITY', c)
    return c
  },
  /**
   * Action to request all communities and store them
   *
   * @function
   *
   * @params {String} ctxt
   *
   * @return {Community[]}
   */
  async getCommunities({ commit }, { ctxt = 'admin' }) {
    const cs = []
    const rsp = await apiCommunities.getCommunities({
      ctxt
    })
    const data = rsp.payload.data
    for (const c of data) {
      cs.push(
        new Community({
          id: c.id,
          name: c.attributes.name,
          rootGroupId: c.relationships.group.data.id
        })
      )
    }

    commit('SET_COMMUNITIES', cs)
    return cs
  },
  /**
   * Action to build a community config with or without ems Information
   *
   * Gets the Community, followed by the GroupStructure under it. Afterwards creating the config and retrieving user information for each group. optionally also ems information for each group
   *
   * @function
   *
   * @params {integer} communityId
   * @params {Store} store
   * @params {Boolean} withEmsInformation, enables ems information fetching
   *
   * @return {Promise}
   */
  async getCommunityStructure({ state, dispatch }, { communityId, store, withEmsInformation = false }) {
    await dispatch('getCommunity', { communityId })

    const tree = await dispatch(
      'energyManagementSystemGroups/getGroupHierarchy',
      { rootId: state[communityId].rootGroupId },
      { root: true }
    )

    const groupIds = getGroupIdsFromTree(tree)

    const calls = []
    calls.push(
      dispatch(
        'energyManagementSystemGroups/getEmsGroups',
        { emsGroupIds: groupIds, type: 'Groups::CommunityGroup' },
        { root: true }
      ).then(() => {
        const callsGroups = []
        callsGroups.push(dispatch('boards/createCommunityBoard', { store }, { root: true }))
        for (const groupId of groupIds) {
          callsGroups.push(dispatch('users/getUsersForGroup', { groupId, removeUnmentioned: false }, { root: true }))
        }
        return Promise.all(callsGroups)
      })
    )

    if (withEmsInformation) {
      for (const groupId of groupIds) {
        calls.push(dispatch('energyManagementSystems/getEmssForGroup', { groupId, withEmsCall: false }, { root: true }))
      }

      calls.push(dispatch('energyManagementSystems/getEmss', {}, { root: true }))
    }
    await Promise.all(calls)

    return groupIds
  },
  /**
   * Action to reset the communities
   *
   * @function
   *
   */
  resetStore({ commit }) {
    commit('RESET_COMMUNITIES')
  },
  /**
   * API call to Change the name of a Community
   *
   * @function
   *
   * @param {String} ctxt
   * @param {integer} communityId
   * @param {String} newCommunityName
   *
   * @return {Promise}
   */
  async changeCommunityName({ commit }, { ctxt = 'admin', communityId, newCommunityName }) {
    return apiCommunities
      .updateCommunity({
        communityId,
        name: newCommunityName,
        ctxt
      })
      .then(() => {
        commit('SET_COMMUNITY_NAME', { communityId, name: newCommunityName })
      })
  },
  /**
   * API call to Add Users as members to a community
   *
   * gets the rootGroupId of a community and adds the users to it
   *
   * @function
   *
   * @param {integer} communityId
   * @param {integer[]} userIds
   *
   * @return {Promise}
   */
  async addUsersToCommunity({ state, dispatch }, { communityId, userIds }) {
    if (state[communityId]) {
      const groupIds = [state[communityId].rootGroupId]

      return dispatch('users/addUsersToGroups', { userIds, groupIds }, { root: true })
    }
  },
  /**
   * API call to remove Users as members from a community
   *
   * gets the rootGroupId of a community and adds the users to it
   *
   * @function
   *
   * @param {integer} communityId
   * @param {integer[]} userIds
   *
   * @return {Promise}
   */
  async removeUsersFromCommunity({ state, dispatch }, { communityId, userIds }) {
    if (state[communityId]) {
      const groupIds = [state[communityId].rootGroupId]
      return dispatch('users/removeUsersFromGroups', { userIds, groupIds }, { root: true })
    }
  },
  /**
   * API call to delete a Community
   *
   * @function
   *
   * @param {String} ctxt
   * @param {integer} communityId
   *
   * @return {Promise}
   */
  async deleteCommunity(_, { ctxt = 'admin', communityId }) {
    return apiCommunities.deleteCommunity({
      ctxt,
      communityId
    })
  },
  /**
   * API call to create a new Community
   *
   * Creates a new CommunityGroup and afterwards creates a Community with it as RootGroup
   *
   * @function
   *
   * @param {String} ctxt
   * @param {String} name
   *
   * @return {Promise}
   */
  async createCommunity({ dispatch }, { ctxt = 'admin', name }) {
    return dispatch(
      'energyManagementSystemGroups/createGroup',
      {
        ctxt,
        name,
        tag: 'TRAFO',
        type: 'Groups::CommunityGroup'
      },
      { root: true }
    ).then((g) => {
      return apiCommunities
        .createCommunity({
          groupId: g.id,
          name
        })
        .then((rsp) => {
          return rsp.payload.data.id
        })
    })
  }
}

/**
 * Helperfunction to parse all GroupIds from a hierarchy Tree
 *
 * @return {integer[]} list of all GroupIds
 */
const getGroupIdsFromTree = (tree) => {
  const keys = Object.keys(tree)
  let res = keys
  for (const key of keys) {
    res = res.concat(getGroupIdsFromTree(tree[key]))
  }

  return res
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
